import { isYouTube, logger, type Embed, type YouTube } from '@tunasong/models'
import invariant from 'tiny-invariant'
import { parsePx, type EmbedSizes } from './types.js'

/** Example URLs
 *
 * https://youtu.be/ifBNDeMZQkI
 * https://youtu.be/ifBNDeMZQkI?t=10
 * https://youtube.com/embed/ifBNDeMZQkI?start=10
 * https://www.youtube.com/embed/ifBNDeMZQkI
 * https://www.youtube.com/watch?v=E1IaC-Ua1w4
 */

const sizes: EmbedSizes = {
  small: {
    width: '300px',
    height: '200px',
  },
  medium: {
    width: '560px',
    height: '315px',
  },
  large: {
    width: '100%',
    height: '500px',
  },
}

const urlShortMatch = /^https:\/\/youtu\.be\/(embed\/|watch\?v=)?([\w-]+)(\?t=)?(\d+)?/
const urlMatch = /^https:\/\/(?:www\.)?youtube\.com\/(embed\/|watch\?v=)([\w-]+)(\?start=)?(\d+)?/

// Add the YouTube iframe API to the global scope
declare global {
  interface Window {
    onYouTubeIframeAPIReady: () => void
    // Tasks to run when the YouTube Iframe API is ready
    onYouTubeIframeAPIReadyTasks: Record<string, () => void>
  }
}

export const parseYouTubeLink = (urlOrUrn?: string): Embed | null => {
  if (!urlOrUrn) {
    return null
  }
  const trimmed = urlOrUrn.trim()
  const match = trimmed.match(urlShortMatch) ?? trimmed.match(urlMatch)
  const mediaId = match?.[2] as string
  const offset = match?.[3] && match?.[4] ? parseInt(match[4], 10) : 0

  if (!mediaId) {
    return null
  }
  const media: YouTube = {
    type: 'embed',
    service: 'youtube',
    url: `https://www.youtube.com/embed/${mediaId}?start=${offset}&enablejsapi=1`,
    mediaId,
    offset,
    size: 'medium',
    children: [{ text: '' }],
  }
  return media
}

const getApi = (embed: Embed, el: HTMLIFrameElement, container: HTMLDivElement) => {
  invariant(isYouTube(embed))
  const scriptId = 'youtube-iframe-api'
  // Check if the youtube-iframe-api script is already present
  if (!document.getElementById(scriptId)) {
    const tag = document.createElement('script')
    tag.id = scriptId
    tag.src = 'https://www.youtube.com/iframe_api'
    const firstScriptTag = document.getElementsByTagName('script')[0]
    invariant(firstScriptTag?.parentNode)
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag)
  }

  const playerEl = document.createElement('div')
  playerEl.id = `youtube-player-${embed.url.replace(/[^a-z0-9]/gi, '-')}`
  container.appendChild(playerEl)

  let player: YT.Player | null = null
  let ready = false

  const updateSize = (embed: Embed) => {
    const size = sizes[embed.size] || sizes.medium

    const widthPx = parsePx(el, size.width, 'width')
    const heightPx = parsePx(el, size.height, 'height')

    player?.setSize(widthPx, heightPx)
  }

  const setupPlayer = () => {
    logger.debug('YouTube Iframe API is ready, executing tasks')
    player = new YT.Player(playerEl, {
      videoId: embed.mediaId,

      events: {
        onReady: ev => {
          logger.debug('YouTube player is ready', ev)
          invariant(player)
          ready = true
        },
        onStateChange: ev => {
          logger.debug('YouTube player state changed', ev)
        },
        onPlaybackRateChange: ev => {
          logger.debug('YouTube player playback rate changed', ev)
        },
      },
    })
    // Required to avoid "Video unavailable" error
    player.getIframe().setAttribute('referrerpolicy', 'origin')
    updateSize(embed)
  }

  // If window.YT exists, the API is already loaded
  if (window.YT && window.YT.Player) {
    setupPlayer()
  } else {
    window.onYouTubeIframeAPIReadyTasks = window.onYouTubeIframeAPIReadyTasks || {}
    window.onYouTubeIframeAPIReadyTasks[embed.url] = setupPlayer

    // We need to handle multiple YouTube video embeds on the same page
    window.onYouTubeIframeAPIReady = () => {
      for (const task of Object.values(window.onYouTubeIframeAPIReadyTasks)) {
        task()
      }
    }
  }

  return {
    updateEmbed: (newEmbed: Embed) => {
      invariant(isYouTube(newEmbed), 'YouTube embed effect update requires a YouTube embed')
      // if (newEmbed.mediaId !== embed.mediaId) {
      //   logger.info('Updating YouTube player with new mediaId', newEmbed.mediaId)
      //   embed = newEmbed
      //   if (player && ready) {
      //     player.loadVideoById({
      //       videoId: newEmbed.mediaId,
      //       startSeconds: newEmbed.offset,
      //     })
      //   } else {
      //     logger.warn('YouTube player is not ready yet')
      //   }
      // }

      updateSize(newEmbed)
    },
    hideIFrame: () => true,
    getCurrentTime: () => {
      if (!(ready && player)) {
        return 0
      }
      return player.getCurrentTime()
    },
    getDuration: () => {
      if (!(ready && player)) {
        return 0
      }
      return player.getDuration()
    },
    setCurrentTime: (time: number) => {
      if (player?.seekTo && ready) {
        player.seekTo(time, true)
      } else {
        logger.warn('YouTube player is not ready yet')
      }
    },
    setPlaybackRate: (rate: number) => {
      if (player?.setPlaybackRate && ready) {
        player.setPlaybackRate(rate)
      } else {
        logger.warn('YouTube player is not ready yet')
      }
    },
    setContainerWidth: (width: number) => {
      if (!player) {
        return
      }
      player.setSize(width, player.getIframe().clientHeight)
    },

    cleanup: () => {
      invariant(isYouTube(embed), 'YouTube embed effect cleanup requires a YouTube embed')
      delete window.onYouTubeIframeAPIReadyTasks[embed.url]

      if (player) {
        player.destroy()
      }
    },
  }
}

export default {
  parse: parseYouTubeLink,
  sizes,
  getApi,
}
